// Angular
import { Injectable } from '@angular/core';

// Libraries
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs';

// Services
import { UnitOfMeasureService } from '../services';
import { CoreService } from '../../../shared/services/core.service';
import { SpinnerService } from '../../../shared/components/spinner/services/spinner.service';

// Models
import { UnitOfMeasureModel } from '../models';
import { AppCleanup } from '../../../shared/models';

// Store
import { UnitOfMeasureActions } from './unit-of-measure.action';

// Enums
import { StatusType } from '../../../shared/enums';
import { append, patch, updateItem } from '@ngxs/store/operators';

export interface UnitOfMeasureStateModel {
	items: UnitOfMeasureModel[];
	loaded: boolean;
}

const defaultState: UnitOfMeasureStateModel = {
	items: undefined,
	loaded: false,
};

@State<any>({
	name: 'unitOfMeasure',
	defaults: defaultState,
})
@Injectable()
export class UnitOfMeasureState extends AppCleanup {
	constructor(
		private unitOfMeasureService: UnitOfMeasureService,
		private spinnerService: SpinnerService,
		private coreService: CoreService,
		private translateService: TranslateService
	) {
		super();
	}

	@Action(UnitOfMeasureActions.GetAll)
	getAll(ctx: StateContext<UnitOfMeasureStateModel>, action: UnitOfMeasureActions.GetAll) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitOfMeasureService
			.getUnitsOfMeasure()
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (unitsOfMeasure) => {
					ctx.setState({
						items: unitsOfMeasure,
						loaded: true,
					});

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}
				},
				error: () => {
					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('GENERIC.ERROR_GETTING', { name: this.translateService.instant('MODULE.UNIT_OF_MEASURE.TITLE_PLURAL') })}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UnitOfMeasureActions.SetAll)
	setAll(ctx: StateContext<UnitOfMeasureStateModel>, action: UnitOfMeasureActions.SetAll) {
		ctx.setState({
			items: action.unitsOfMeasure,
			loaded: true,
		});
	}

	@Action(UnitOfMeasureActions.Create)
	create(ctx: StateContext<UnitOfMeasureStateModel>, action: UnitOfMeasureActions.Create) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitOfMeasureService
			.createUnitOfMeasure(action.data.unitOfMeasure)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (unitOfMeasure) => {
					let newUnitOfMeasure = <UnitOfMeasureModel>{
						id: unitOfMeasure.id,
						name: unitOfMeasure.name,
						description: unitOfMeasure.description,
					};

					ctx.setState(
						patch({
							items: append([newUnitOfMeasure]),
						})
					);

					ctx.dispatch(new UnitOfMeasureActions.CreateSuccess(newUnitOfMeasure));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('MODULE.UNIT_OF_MEASURE.CREATED')}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new UnitOfMeasureActions.CreateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('MODULE.UNIT_OF_MEASURE.NOT_CREATED')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Action(UnitOfMeasureActions.Update)
	update(ctx: StateContext<UnitOfMeasureStateModel>, action: UnitOfMeasureActions.Update) {
		if (action.data.showSpinner) {
			this.spinnerService.showAppSpinner(); // Turns on app wide spinner
		}

		return this.unitOfMeasureService
			.updateUnitOfMeasure(action.data.unitOfMeasure)
			.pipe(takeUntil(this.destroy))
			.subscribe({
				next: (unitOfMeasure) => {
					let updatedUnitOfMeasure = <UnitOfMeasureModel>{
						id: unitOfMeasure.id,
						name: unitOfMeasure.name,
						description: unitOfMeasure.description,
					};

					const currentState = ctx.getState();
					const existingItem = currentState.items?.find((item) => item.id === unitOfMeasure.id);

					if (existingItem) {
						ctx.setState(
							patch({
								items: updateItem((unitOfMeasure) => unitOfMeasure.id === action.data.unitOfMeasure.id, updatedUnitOfMeasure),
							})
						);
					}

					ctx.dispatch(new UnitOfMeasureActions.UpdateSuccess(updatedUnitOfMeasure));

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('MODULE.UNIT_OF_MEASURE.UPDATED')}`;
						this.coreService.toast({ message: message, type: StatusType.SUCCESS });
					}
				},
				error: () => {
					ctx.dispatch(new UnitOfMeasureActions.UpdateError());

					if (action.data.showSpinner) {
						this.spinnerService.hideAppSpinner(); // Turns off app wide spinner
					}

					if (action.data.showToast) {
						const message = `${this.translateService.instant('MODULE.UNIT_OF_MEASURE.NOT_UPDATED')}`;
						this.coreService.toast({ message: message, type: StatusType.DANGER });
					}
				},
			});
	}

	@Selector()
	static isLoaded(state: UnitOfMeasureStateModel): boolean {
		return state.loaded;
	}

	@Selector()
	static unitsOfMeasure(state: UnitOfMeasureStateModel): UnitOfMeasureModel[] {
		return state.items;
	}
}
